import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { CustomListItem } from "../../../components/ListItem";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useEffect, useMemo, useState } from "react";
import {
  CheckList,
  CheckListTemplateDataInner,
  CreateChecklistRequest,
  EmployeeDetail,
  SettingRepeat,
  Task,
  TasksApi,
  TaskTemplate,
  UpdateChecklistRequest,
  UpdateChecklistRequestTasksInner,
  UserApi,
} from "../../../../../services/phpServices";
import NewTask from "./NewTaskModal";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ImportCListModal from "../../Templates/ImportCListModal";
import moment from "moment-timezone";
import { Edit, KeyboardArrowDown, Visibility } from "@mui/icons-material";
import RepeatSettings from "./RepeatSetting";
import { formatDate } from "../../../../../common/dateTime";
import { Colors } from "../../../../../theme";
import { useTranslation } from "react-i18next";
import { useStores } from "../../../../../models/helpers/useStores";
import EditTask from "./EditTask";

interface NewChecklistModalProps {
  open: boolean;
  onClose: () => void;
  time?: number;
  onConfirm?: () => void;
  id?: number;
}

const ChecklistModal: React.FC<NewChecklistModalProps> = ({
  open,
  onClose,
  time,
  onConfirm,
  id,
}) => {
  const [emps, setEmps] = useState<EmployeeDetail[]>();
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [openEditTaskModal, setOpenEditTaskModal] = useState(false);
  const [openSettingRepeatModal, setOpenSettingRepeatModal] = useState(false);
  const [data, setData] = useState<CheckList>();
  const [showImportClist, setShowImportClist] = useState(false);
  const [isFocusedName, setIsFocusedName] = useState(false);
  const [isFocusedEmp, setIsFocusedEmp] = useState(false);
  const { t } = useTranslation() as any;
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number | null>(
    null
  );

  const {
    appStore: { userProfile, getACLGroup },
  } = useStores();

  const defaultParam: CreateChecklistRequest = {
    check_list_id: 0,
    name: "",
    start_time: time ? moment.unix(time).unix() : moment().unix(),
    end_time: time ? moment.unix(time).unix() : moment().unix(),
    status: "PUBLISH",
    setting_repeat: undefined,
    employees: [],
    tasks: [],
    is_create_completed: false,
  };

  const transformTasks = (
    tasks: Task[] = []
  ): UpdateChecklistRequestTasksInner[] => {
    return tasks.map((task) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      photo: task.photo,
      note: task.note,
      checkin: task.checkin,
      status: task.status,
      photo_from_library: task.photo_from_library,
      require_time: task.require_time,
    }));
  };

  const defaultEditParam: UpdateChecklistRequest = {
    check_list_id: data?.check_list_id,
    name: data?.name,
    start_time: data?.start_time
      ? moment.unix(data?.start_time).unix()
      : moment().unix(),
    end_time: data?.end_time
      ? moment.unix(data?.end_time).unix()
      : moment().unix(),
    status: data?.status,
    employees: data?.employees?.map((employee) =>
      Number(employee?.employee_id)
    ),
    setting_repeat: {
      ...(data?.setting_repeat as SettingRepeat),
      repeat_type: data?.setting_repeat?.repeat_type ?? 1,
      edit_date_start: moment
        .unix(time ?? 0)
        .startOf("day")
        .unix(),
    },
    tasks: transformTasks(data?.tasks) ?? [],
  };

  const { aclChecklist } = useMemo(() => {
    const aclGroup = getACLGroup()?.[userProfile?.PrimaryGroup?.GroupId ?? 0];
    return {
      aclChecklist: aclGroup?.task,
    };
  }, [userProfile]);

  useEffect(() => {
    if (id !== 0) {
      const fetchData = async () => {
        try {
          const res = await new TasksApi().detailChecklist(
            id ?? 0,
            "web",
            moment
              .unix(time ?? 0)
              .startOf("day")
              .unix(),
            moment
              .unix(time ?? 0)
              .endOf("day")
              .unix()
          );
          setData(res.data);
        } catch (e) {
          console.log(e);
        }
      };
      fetchData();
    }
  }, [open]);

  const [params, setParams] = useState<
    CreateChecklistRequest | UpdateChecklistRequest
  >(Number(id) === 0 ? defaultParam : defaultEditParam);

  const repeatType = [
    { value: 0, label: t("26_00_all") },
    {
      value: 1,
      label: `${t("26_00_this")} (${moment
        .unix(time ?? 0)
        .format("DD/MM/YYYY")})`,
    },
    {
      value: 2,
      label: `${t("26_00_this_and_following")} (${moment
        .unix(time ?? 0)
        .format("DD/MM/YYYY")} - ${moment
        .unix(params.end_time ?? 0)
        .format("DD/MM/YYYY")})`,
    },
  ];

  const selectedEmployees =
    emps?.filter((e) => params?.employees?.includes(Number(e.employee_id))) ||
    [];

  useEffect(() => {
    if (data && id !== 0) {
      setParams(defaultEditParam);
    }
    if (id === 0) {
      setParams(defaultParam);
    }
  }, [data, id]);

  async function getEmps() {
    try {
      const res = await new UserApi().getListEmployee({
        name: "",
        order: 0,
        page: 1,
        limit: 10000,
        status: [1, 4],
        groups: [],
        type_object: id === 0 ? "createChecklist" : "updateChecklist",
      });
      setEmps(res.data.data);
    } catch (e) {
      console.log(e);
    }
  }

  const handleClose = () => {
    if (id === 0) {
      setParams(defaultParam);
    } else {
      setParams(defaultEditParam);
    }
    onClose();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setParams({
      ...params,
      name: event.target.value,
    });
  };

  const handleStartDateChange = (date: any) => {
    if (params.setting_repeat?.repeat === 1) {
      const newEndTime =
        params.setting_repeat?.frequency === "daily" &&
        params.setting_repeat?.end_repeat === "after_x_occurrences"
          ? moment(date)
              .add(Number(params.setting_repeat?.end_repeat_value), "day")
              .unix()
          : params.setting_repeat?.frequency === "weekly" &&
            params.setting_repeat?.end_repeat === "after_x_occurrences"
          ? moment(date)
              .add(params.setting_repeat?.end_repeat_value, "week")
              .unix()
          : params.setting_repeat?.frequency === "monthly" &&
            params.setting_repeat?.end_repeat === "after_x_occurrences"
          ? moment(date)
              .add(params.setting_repeat?.end_repeat_value, "month")
              .unix()
          : params.end_time;
      setParams({
        ...params,
        start_time: moment(date).unix(),
        end_time: newEndTime,
      });
    } else {
      setParams({
        ...params,
        start_time: moment(date).startOf("day").unix(),
        end_time: moment(date).endOf("day").unix(),
      });
    }
  };

  const handleEndDateChange = (date: any) => {
    if (params?.setting_repeat?.repeat === 1) {
      setParams({
        ...params,
        end_time: moment(date).unix(),
        setting_repeat: {
          ...params.setting_repeat,
          end_date: moment(date).unix(),
        },
      });
    } else {
      setParams({
        ...params,
        start_time: moment(date).startOf("day").unix(),
        end_time: moment(date).endOf("day").unix(),
      });
    }
  };

  const handleTaskConfirmed = (t: Task) => {
    setParams((prevParams) => ({
      ...prevParams,
      tasks: [...(prevParams?.tasks ?? []), t],
    }));
  };

  const handleImportClist = (cList: CheckListTemplateDataInner) => {
    setParams((prevParams) => ({
      ...prevParams,
      name:
        prevParams.name !== ""
          ? prevParams.name
          : cList.name ?? prevParams.name,
      tasks: [...(prevParams?.tasks ?? []), ...(cList?.tasks ?? [])],
    }));
  };

  const handleImportTasks = (t: TaskTemplate[]) => {
    setParams((prevParams) => ({
      ...prevParams,
      tasks: [...(prevParams?.tasks ?? []), ...(t ?? [])],
    }));
  };

  const handleRemoveTask = (taskIndex: number) => {
    setParams((prevParams) => ({
      ...prevParams,
      tasks: prevParams?.tasks?.filter((_, index) => index !== taskIndex),
    }));
  };

  async function createChecklist() {
    try {
      setLoading(true);
      const apiParams: CreateChecklistRequest | UpdateChecklistRequest = {
        ...params,
        start_time: moment
          .unix(params.start_time ?? 0)
          .startOf("day")
          .unix(),
        end_time: moment
          .unix(params.end_time ?? 0)
          .endOf("day")
          .unix(),
      };
      if (id === 0) {
        await new TasksApi().createChecklist(apiParams);
      } else {
        await new TasksApi().updateChecklist(id ?? 0, apiParams);
      }
      if (onConfirm) {
        setLoading(false);
        onConfirm();
      }
    } catch (e) {
      console.log(e);
    } finally {
      handleClose();
    }
  }

  useEffect(() => {
    getEmps();
  }, []);

  const WEEK_DAYS = [
    t("00_04_monday"),
    t("00_04_tuesday"),
    t("00_04_wednesday"),
    t("00_04_thusday"),
    t("00_04_friday"),
    t("00_04_saturday"),
    t("00_04_sunday"),
  ];

  const getOrdinal = (n: number) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
  };
  const getWeekTx = (frequencyValues: number[]) => {
    return frequencyValues
      .sort((a, b) => a - b)
      .map((day) => WEEK_DAYS[day - 1])
      .join(", ");
  };

  const getMonthlyTx = (frequencyValues: number[]) => {
    return frequencyValues
      .sort((a, b) => a - b)
      .map(getOrdinal)
      .join(", ");
  };
  const getRepeatText = (params: any) => {
    if (params?.setting_repeat?.repeat === 0) {
      return t("03_02_none");
    }
    const {
      frequency,
      end_repeat,
      end_date,
      end_repeat_value,
      frequency_values,
    } = params?.setting_repeat ?? {};
    if (frequency === "daily") {
      return end_repeat === "until_date"
        ? `${t("00_04_daily")} ${formatDate(end_date ?? 0)}`
        : `${t("00_04_daily")} ${end_repeat_value} ${t("26_00_occurrences")}`;
    }
    if (frequency === "weekly") {
      return end_repeat === "until_date"
        ? `${t("26_00_weekly_on")} ${getWeekTx(frequency_values)} ${t(
            "00_06_until"
          )} ${formatDate(end_date ?? 0)}`
        : `${t("26_00_weekly_on")} ${getWeekTx(frequency_values)} ${t(
            "00_06_after"
          )} ${end_repeat_value} ${t("26_00_occurrences")}`;
    }
    if (frequency === "monthly") {
      return end_repeat === "until_date"
        ? `${t("26_00_monthly_on")} ${getMonthlyTx(frequency_values)} ${t(
            "00_06_until"
          )} ${formatDate(end_date ?? 0)}`
        : `${t("26_00_monthly_on")} ${getMonthlyTx(frequency_values)} ${t(
            "00_06_after"
          )} ${end_repeat_value} ${t("26_00_occurrences")}`;
    }
    return t("03_02_none");
  };

  return (
    <>
      <Modal open={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: 24,
            width: "600px",
            maxWidth: "600px",
            maxHeight: "80vh",
            transform: "t(-50%, -50%)",
            overflow: "hidden",
          }}
        >
          <>
            {/* Modal Header */}
            <Box
              sx={{
                height: "40px",
                backgroundColor: "#08B0A0",
                display: "flex",
                alignItems: "center",
                borderRadius: "4px 4px 0 0",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{ paddingLeft: "16px" }}
                variant="h5"
                color={Colors.white}
              >
                {id === 0
                  ? t("26_00_new_checklist").toUpperCase()
                  : t("00_03_edit").toUpperCase()}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon
                  sx={{ color: Colors.white, width: 24, height: 24 }}
                />
              </IconButton>
            </Box>

            {/* Modal Content */}
            <Box
              sx={{
                paddingLeft: "20px",
                paddingRight: "20px",
                paddingBottom: "20px",
                overflowY: "auto",
                maxHeight: "calc(80vh - 70px)",
                marginTop: "8px",
              }}
            >
              <CustomListItem
                leftContainerWidth={150}
                text={`${t("26_00_name")} *`}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <TextField
                    id="cL-name"
                    label={t("26_00_name")}
                    variant="standard"
                    style={{ width: "100%" }}
                    onFocus={() => setIsFocusedName(true)}
                    onBlur={(e) => {
                      if (!e.target.value) setIsFocusedName(false);
                    }}
                    inputProps={{ maxLength: 200 }}
                    InputProps={{
                      style: {
                        fontSize: "13px",
                        fontWeight: "400",
                      },
                    }}
                    value={params.name}
                    onChange={handleNameChange}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: "12px",
                        fontWeight: "400",
                        opacity:
                          (isFocusedName && id === 0) || id !== 0 ? 0 : 1,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "13px",
                        fontWeight: "400",
                      },
                    }}
                  />
                }
              />
              <CustomListItem
                containerStyle={{ marginBottom: "16px" }}
                leftContainerWidth={150}
                text={t("22_00_period")}
                RightComponent={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      gap: "40px",
                      width: "100%",
                    }}
                  >
                    <DesktopDatePicker
                      value={moment.unix(params?.start_time ?? 0)}
                      format="DD/MM/YYYY"
                      onChange={handleStartDateChange}
                      slots={{ openPickerIcon: KeyboardArrowDown }}
                      sx={{ width: "50%" }}
                      maxDate={moment.unix(params?.end_time ?? 0)}
                      disabled={id !== 0}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          style: { width: "100%", fontSize: "13px" },
                          InputLabelProps: { size: "small" },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",
                              fontWeight: "400",
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "13px",
                              fontWeight: "400",
                            },
                          },
                        },
                      }}
                    />
                    <DesktopDatePicker
                      value={moment.unix(params?.end_time ?? 0)}
                      onChange={handleEndDateChange}
                      format="DD/MM/YYYY"
                      disabled={
                        params?.setting_repeat?.end_repeat ===
                        "after_x_occurrences"
                      }
                      minDate={moment.unix(params?.start_time ?? 0)}
                      slots={{ openPickerIcon: KeyboardArrowDown }}
                      sx={{ width: "50%" }}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          style: { width: "100%", fontSize: "13px" },
                          InputLabelProps: { size: "small" },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",
                              fontWeight: "400",
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "13px",
                              fontWeight: "400",
                            },
                          },
                        },
                      }}
                    />
                  </div>
                }
              />
              <CustomListItem
                leftContainerWidth={150}
                text={t("03_01_repeat")}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <TextField
                    id="cL-repeat"
                    label={t("26_00_repeat_setting")}
                    variant="standard"
                    style={{ width: "100%" }}
                    InputProps={{
                      style: {
                        fontSize: "13px",
                        fontWeight: "400",
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setOpenSettingRepeatModal(true);
                            }}
                            disabled={
                              !!(params as CreateChecklistRequest)
                                .is_create_completed
                            }
                          >
                            {id === 0 && (
                              <Edit style={{ width: 20, height: 20 }} />
                            )}
                            {id !== 0 && (
                              <Visibility style={{ width: 20, height: 20 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      style: {
                        fontSize: "12px",
                        fontWeight: "400",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        color: "#212121 !important",
                        WebkitTextFillColor: "#212121 !important",
                      },
                    }}
                    value={getRepeatText(params)}
                    disabled
                  />
                }
              />
              {id !== 0 && params.setting_repeat?.repeat === 1 && (
                <CustomListItem
                  leftContainerWidth={150}
                  text={t("03_01_repeat_type")}
                  containerStyle={{ marginBottom: "16px" }}
                  RightComponent={
                    <FormControl fullWidth>
                      <Autocomplete
                        id={t("03_01_repeat_type")}
                        onChange={(event, newValue) => {
                          setParams({
                            ...params,
                            setting_repeat: {
                              ...(params.setting_repeat as SettingRepeat),
                              repeat_type: newValue?.value ?? 1,
                            },
                          });
                        }}
                        value={
                          repeatType.find(
                            (opt) =>
                              Number(opt.value) ===
                              Number(params.setting_repeat?.repeat_type ?? 1)
                          ) || repeatType[1]
                        }
                        options={repeatType}
                        getOptionLabel={(option) => option.label}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            label=""
                            sx={{
                              "& .MuiInputLabel-root": {
                                display: "none",
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "13px",
                                fontWeight: "400",
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{ fontSize: "13px", fontWeight: "400" }}
                          >
                            {option.label}
                          </li>
                        )}
                      />
                    </FormControl>
                  }
                />
              )}
              <RepeatSettings
                open={openSettingRepeatModal}
                onClose={() => setOpenSettingRepeatModal(false)}
                startTime={params.start_time ?? 0}
                endTime={params.end_time ?? 0}
                onConfirmed={(s) => {
                  setParams({
                    ...params,
                    setting_repeat: s,
                    end_time: moment.unix(s.end_date ?? 0).unix(),
                  });
                }}
                settings={params.setting_repeat}
                isEdit={id !== 0 ? true : false}
              />
              <CustomListItem
                leftContainerWidth={150}
                text={t("26_00_publish")}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <FormControl>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Switch
                        checked={params.status === "PUBLISH"}
                        onChange={(event) => {
                          setParams({
                            ...params,
                            status: event.target.checked
                              ? "PUBLISH"
                              : "UNPUBLISH",
                          });
                        }}
                        inputProps={{ "aria-label": "Publish status toggle" }}
                        color="primary"
                      />
                    </Box>
                  </FormControl>
                }
              />
              <CustomListItem
                leftContainerWidth={150}
                text={`${t("26_00_assigned_to")} *`}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <FormControl fullWidth>
                    <Autocomplete
                      multiple
                      id="assigned-to"
                      options={emps || []}
                      getOptionLabel={(option) => option.fullname ?? ""}
                      value={
                        emps?.filter((e) =>
                          params?.employees?.includes(Number(e.employee_id))
                        ) || []
                      }
                      onChange={(event, newValue) => {
                        const selectedIds =
                          newValue?.map((e) => Number(e.employee_id)) ?? [];
                        setParams({
                          ...params,
                          employees: selectedIds,
                        });
                      }}
                      disableCloseOnSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label={t("21_00_select_employees")}
                          onFocus={() => setIsFocusedEmp(true)}
                          onBlur={() => {
                            if (
                              !params.inputProps?.value &&
                              selectedEmployees.length === 0
                            ) {
                              setIsFocusedEmp(false);
                            }
                          }}
                          sx={{
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",
                              fontWeight: "400",
                              opacity:
                                isFocusedEmp || selectedEmployees.length > 0
                                  ? 0
                                  : 1,
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "13px",
                              fontWeight: "400",
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) => {
                        return value.map((option, index) => (
                          <span
                            key={Number(option.employee_id)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              margin: "2px",
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                display: "inline-block",
                                marginRight: "6px",
                              }}
                            >
                              {option.avatar ? (
                                <img
                                  src={option.avatar}
                                  alt={option.fullname}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <span
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: option.color || "#ccc",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    userSelect: "none",
                                  }}
                                >
                                  {option.fullname?.[0] || ""}
                                </span>
                              )}
                              {Number(option.status) === 4 && (
                                <span
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: 6,
                                    height: 6,
                                    background: "#f5a623",
                                    borderRadius: "50%",
                                    border: "1px solid #fff",
                                  }}
                                />
                              )}
                            </Box>
                            <Typography style={{ fontSize: "13px" }}>
                              {option.fullname}
                              {index < value.length - 1 && ", "}
                            </Typography>
                          </span>
                        ));
                      }}
                      renderOption={(props, option, state) => (
                        <li
                          {...props}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            checked={params?.employees?.some(
                              (id) => Number(id) === Number(option.employee_id)
                            )}
                          />
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-block",
                              marginRight: "6px",
                            }}
                          >
                            {option.avatar ? (
                              <img
                                src={option.avatar}
                                alt={option.fullname}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  background: option.color || "#ccc",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  userSelect: "none",
                                }}
                              >
                                {option.fullname?.[0] || ""}
                              </span>
                            )}
                            {Number(option.status) === 4 && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: 8,
                                  height: 8,
                                  background: "#f5a623",
                                  borderRadius: "50%",
                                  border: "1px solid #fff",
                                }}
                              />
                            )}
                          </Box>
                          <Typography>{option.fullname}</Typography>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        Number(option.employee_id) === Number(value.employee_id)
                      }
                    />
                  </FormControl>
                }
              />
              <CustomListItem
                leftContainerWidth={150}
                containerStyle={{ marginBottom: id === 0 ? "16px" : "100px" }}
                noCentered={true}
                text={`${t("00_01_tasks")} *`}
                RightComponent={
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    <List>
                      {params?.tasks?.map((t, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            display: "flex",
                          }}
                        >
                          <AssignmentOutlinedIcon
                            sx={{
                              color: "gray",
                              width: 20,
                              height: 20,
                              marginRight: "12px",
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "13px", fontWeight: "400" }}
                          >
                            {t.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginLeft: "auto",
                            }}
                          >
                            {(!t.task_id ||
                              t.task_id === 0 ||
                              id === 0 ||
                              (id !== 0 &&
                                data?.tasks?.some(
                                  (task) =>
                                    Number(task.task_id) ===
                                      Number(t.task_id) &&
                                    task.complete_status !== "COMPLETED"
                                ))) && (
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  await setSelectedTask(t);
                                  setSelectedTaskIndex(index);
                                  setOpenEditTaskModal(true);
                                }}
                                sx={{ marginRight: "4px" }}
                              >
                                <EditOutlinedIcon
                                  sx={{ color: "gray", width: 20, height: 20 }}
                                />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={() => handleRemoveTask(index)}
                              sx={{
                                marginLeft: "auto",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              {(!t.task_id ||
                                t.task_id === 0 ||
                                id === 0 ||
                                (id !== 0 &&
                                  data?.tasks?.some(
                                    (task) =>
                                      Number(task.task_id) ===
                                        Number(t.task_id) &&
                                      task.complete_status !== "COMPLETED"
                                  ))) && (
                                <DeleteIcon
                                  sx={{ color: "gray", width: 20, height: 20 }}
                                />
                              )}
                            </IconButton>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                    <Box
                      sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        style={{
                          color: "rgb(82, 146, 82)",
                          borderColor: "rgb(69, 139, 69)",
                          marginRight: "20px",
                          fontSize: "13px",
                          maxHeight: "36px",
                        }}
                        onClick={() => setOpenTaskModal(true)}
                      >
                        {t("00_03_add")}
                      </Button>
                      <Button
                        variant="outlined"
                        style={{
                          color: "rgb(82, 104, 146)",
                          borderColor: "rgb(69, 92, 139)",
                          fontSize: "13px",
                          maxHeight: "36px",
                        }}
                        onClick={() => setShowImportClist(true)}
                      >
                        {t("00_03_import")}
                      </Button>
                    </Box>
                  </Box>
                }
              />
              {id === 0 && aclChecklist?.Approve === true && (
                <CustomListItem
                  leftContainerWidth={150}
                  containerStyle={{ marginBottom: "100px" }}
                  noCentered={true}
                  text={t("20_00_complete")}
                  RightComponent={
                    <Checkbox
                      checked={
                        !!(params as CreateChecklistRequest).is_create_completed
                      }
                      onChange={(e) =>
                        setParams({
                          ...params,
                          is_create_completed: e.target.checked,
                          setting_repeat: e.target.checked
                            ? null
                            : params.setting_repeat,
                          end_time: moment
                            .unix(params.start_time)
                            .endOf("day")
                            .unix(),
                        })
                      }
                      color="primary"
                      sx={{ padding: 0 }}
                    />
                  }
                />
              )}

              <NewTask
                open={openTaskModal}
                onClose={() => setOpenTaskModal(false)}
                onConfirmed={handleTaskConfirmed}
                checkedComplete={
                  (params as CreateChecklistRequest).is_create_completed
                }
              />

              {selectedTask !== null && openEditTaskModal && (
                <EditTask
                  open={true}
                  onClose={() => {
                    setOpenEditTaskModal(false);
                    setSelectedTask(null);
                    setSelectedTaskIndex(null);
                  }}
                  taskData={selectedTask}
                  onConfirmed={(editedTask) => {
                    setParams((prevParams) => {
                      const updatedTasks = [...prevParams.tasks];
                      if (
                        selectedTaskIndex !== null &&
                        selectedTaskIndex >= 0 &&
                        selectedTaskIndex < updatedTasks.length
                      ) {
                        updatedTasks[selectedTaskIndex] = editedTask;
                      }
                      return { ...prevParams, tasks: updatedTasks };
                    });
                    setOpenEditTaskModal(false);
                    setSelectedTask(null);
                    setSelectedTaskIndex(null);
                  }}
                />
              )}

              {/* Modal Button */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "0",
                  right: "0",
                  display: "flex",
                  gap: "20px",
                  backgroundColor: "white",
                  padding: "20px 20px",
                  borderRadius: "4px",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  style={{
                    color: "gray",
                    borderColor: "gray",
                    fontSize: "13px",
                    maxHeight: "36px",
                  }}
                  onClick={handleClose}
                >
                  {t("00_03_cancel")}
                </Button>
                <Button
                  variant="contained"
                  style={{ fontSize: "13px", maxHeight: "36px" }}
                  onClick={createChecklist}
                  disabled={
                    params.name === "" ||
                    Number(params.end_time) < Number(params.start_time) ||
                    params.employees?.length === 0 ||
                    (params.tasks?.length === 0 && id === 0) ||
                    loading
                  }
                >
                  {t("00_03_save")}
                </Button>
              </Box>
            </Box>
          </>
          <ImportCListModal
            open={showImportClist}
            onClose={() => setShowImportClist(false)}
            onConfirmed={handleImportClist}
            onConfirmedTask={handleImportTasks}
          />
        </Box>
      </Modal>
    </>
  );
};
export default ChecklistModal;
